import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack
} from '@mui/material';
import { Save, Close, Edit } from '@mui/icons-material';
import questionnairesService from '../../../services/questionnairesService';

const EditQuestionnaireModal = ({ open, onClose, questionnaireId }) => {
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [updatingQuestions, setUpdatingQuestions] = useState(new Set());

  useEffect(() => {
    if (open && questionnaireId) {
      fetchQuestionnaire();
    }
  }, [open, questionnaireId]);

  const fetchQuestionnaire = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await questionnairesService.getQuestionnaireById(questionnaireId);
      setQuestionnaire(data);
    } catch (err) {
      setError('Error al cargar el cuestionario');
      console.error('Error fetching questionnaire:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionChange = (questionId, field, value) => {
    if (!questionnaire) return;

    const questionIndex = questionnaire.questions.findIndex(q => q.id === questionId);
    if (questionIndex === -1) return;

    // Actualizar el estado local inmediatamente
    const updatedQuestions = [...questionnaire.questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      [field]: value
    };

    setQuestionnaire({
      ...questionnaire,
      questions: updatedQuestions
    });
  };

  const handleQuestionUpdate = async (questionId) => {
    if (!questionnaire) return;

    const questionIndex = questionnaire.questions.findIndex(q => q.id === questionId);
    if (questionIndex === -1) return;

    // Marcar la pregunta como actualizando
    setUpdatingQuestions(prev => new Set(prev).add(questionId));

    try {
      const question = questionnaire.questions[questionIndex];
      // Preparar los datos de la pregunta
      const questionData = {
        id: questionId,
        questionText: question.questionText,
        optionA: question.optionA,
        optionB: question.optionB,
        optionC: question.optionC,
        optionD: question.optionD,
        correctAnswer: question.correctAnswer,
        difficulty: question.difficulty // Se mantiene pero no se puede editar
      };

      await questionnairesService.updateQuestion(questionId, questionData);
      setSuccessMessage('Pregunta actualizada correctamente');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating question:', err);
      setError('Error al actualizar la pregunta');
      setTimeout(() => setError(null), 3000);
      
      // Revertir cambios en caso de error
      fetchQuestionnaire();
    } finally {
      setUpdatingQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
    }
  };

  const handleClose = () => {
    setQuestionnaire(null);
    setError(null);
    setSuccessMessage('');
    setUpdatingQuestions(new Set());
    onClose();
  };

  const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
      case 0: return 'Fácil';
      case 1: return 'Medio';
      case 2: return 'Difícil';
      default: return 'Fácil';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 0: return 'success';
      case 1: return 'warning';
      case 2: return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box className="flex flex-col items-center justify-center py-8 gap-4">
            <CircularProgress />
            <Typography>Cargando cuestionario...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle className="background-green-aqua flex items-center justify-between">
        <Typography variant="h6" className="text-white font-semibold">
          <Edit className="mr-2" />
          Editar Cuestionario: {questionnaire?.title}
        </Typography>
        <Button onClick={handleClose} className="text-white hover:bg-white hover:bg-opacity-20 transition-all">
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent dividers className="max-h-[80vh] overflow-y-auto">
        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" className="mb-4">
            {successMessage}
          </Alert>
        )}

        {questionnaire && (
          <Box className="space-y-6">
            {/* Información del cuestionario */}
            <Card className="border-l-4 border-green-aqua shadow-lg">
              <CardContent className="bg-gradient-to-r from-green-50 to-blue-50">
                <Typography variant="h6" className="mb-4 text-green-aqua font-bold flex items-center">
                  <Box className="w-2 h-6 bg-green-aqua rounded mr-3"></Box>
                  Información del Cuestionario
                </Typography>
                <Stack spacing={3}>
                  <Box className="bg-white p-3 rounded-lg shadow-sm">
                    <Typography className="text-gray-600 text-sm font-medium">Título</Typography>
                    <Typography className="text-gray-800 font-semibold">{questionnaire.title}</Typography>
                  </Box>
                  <Box className="bg-white p-3 rounded-lg shadow-sm">
                    <Typography className="text-gray-600 text-sm font-medium">Descripción</Typography>
                    <Typography className="text-gray-800">{questionnaire.description}</Typography>
                  </Box>
                  <Box className="bg-white p-3 rounded-lg shadow-sm">
                    <Typography className="text-gray-600 text-sm font-medium">Creado por</Typography>
                    <Typography className="text-gray-800 font-semibold">{questionnaire.uploadedBy?.name}</Typography>
                  </Box>
                  <Box className="bg-white p-3 rounded-lg shadow-sm">
                    <Typography className="text-gray-600 text-sm font-medium">Total de preguntas</Typography>
                    <Chip 
                      label={questionnaire.questions?.length || 0} 
                      className="background-green-aqua text-white font-bold"
                      size="small"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Divider />

            {/* Lista de preguntas */}
            <Box>
              <Typography variant="h6" className="mb-4 text-green-aqua font-bold flex items-center">
                <Box className="w-2 h-6 bg-green-aqua rounded mr-3"></Box>
                Preguntas ({questionnaire.questions?.length || 0})
              </Typography>
              
              <Stack spacing={4}>
                {questionnaire.questions?.map((question, index) => (
                  <Card key={question.id} className="border-l-4 border-green-aqua shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="bg-gradient-to-br from-white to-gray-50">
                      <Box className="flex items-center justify-between mb-4">
                        <Box className="flex items-center gap-3">
                          <Box className="w-8 h-8 bg-green-aqua rounded-full flex items-center justify-center">
                            <Typography className="text-white font-bold text-sm">
                              {index + 1}
                            </Typography>
                          </Box>
                          <Typography variant="subtitle1" className="font-bold text-gray-800">
                            Pregunta {index + 1}
                          </Typography>
                        </Box>
                        <Box className="flex items-center gap-3">
                          <Chip
                            label={getDifficultyLabel(question.difficulty)}
                            color={getDifficultyColor(question.difficulty)}
                            size="small"
                            className="font-semibold"
                          />
                          {updatingQuestions.has(question.id) && (
                            <CircularProgress size={20} className="text-green-aqua" />
                          )}
                        </Box>
                      </Box>

                      <Stack spacing={3}>
                        {/* Pregunta */}
                        <Box className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <Typography className="text-gray-700 font-medium mb-2">Pregunta</Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={question.questionText}
                            onChange={(e) => handleQuestionChange(question.id, 'questionText', e.target.value)}
                            onBlur={() => handleQuestionUpdate(question.id)}
                            disabled={updatingQuestions.has(question.id)}
                            variant="outlined"
                            className="bg-gray-50"
                          />
                        </Box>

                        {/* Opciones */}
                        <Box className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <Typography className="text-gray-700 font-medium mb-3">Opciones de Respuesta</Typography>
                          <Box className="grid grid-cols-2 gap-4">
                            <TextField
                              label="Opción A"
                              value={question.optionA}
                              onChange={(e) => handleQuestionChange(question.id, 'optionA', e.target.value)}
                              onBlur={() => handleQuestionUpdate(question.id)}
                              disabled={updatingQuestions.has(question.id)}
                              variant="outlined"
                              className="bg-gray-50"
                            />
                            <TextField
                              label="Opción B"
                              value={question.optionB}
                              onChange={(e) => handleQuestionChange(question.id, 'optionB', e.target.value)}
                              onBlur={() => handleQuestionUpdate(question.id)}
                              disabled={updatingQuestions.has(question.id)}
                              variant="outlined"
                              className="bg-gray-50"
                            />
                            <TextField
                              label="Opción C"
                              value={question.optionC}
                              onChange={(e) => handleQuestionChange(question.id, 'optionC', e.target.value)}
                              onBlur={() => handleQuestionUpdate(question.id)}
                              disabled={updatingQuestions.has(question.id)}
                              variant="outlined"
                              className="bg-gray-50"
                            />
                            <TextField
                              label="Opción D"
                              value={question.optionD}
                              onChange={(e) => handleQuestionChange(question.id, 'optionD', e.target.value)}
                              onBlur={() => handleQuestionUpdate(question.id)}
                              disabled={updatingQuestions.has(question.id)}
                              variant="outlined"
                              className="bg-gray-50"
                            />
                          </Box>
                        </Box>

                        {/* Respuesta correcta */}
                        <Box className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <Typography className="text-gray-700 font-medium mb-3">Respuesta Correcta</Typography>
                          <FormControl fullWidth>
                            <InputLabel>Selecciona la respuesta correcta</InputLabel>
                            <Select
                              value={question.correctAnswer}
                              label="Selecciona la respuesta correcta"
                              onChange={(e) => handleQuestionChange(question.id, 'correctAnswer', e.target.value)}
                              onBlur={() => handleQuestionUpdate(question.id)}
                              disabled={updatingQuestions.has(question.id)}
                              className="bg-gray-50"
                            >
                              <MenuItem value="A">A</MenuItem>
                              <MenuItem value="B">B</MenuItem>
                              <MenuItem value="C">C</MenuItem>
                              <MenuItem value="D">D</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="p-4 bg-gray-50">
        <Button 
          onClick={handleClose} 
          variant="outlined"
          className="border-green-aqua text-green-aqua hover:bg-green-aqua hover:text-white transition-all duration-200"
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditQuestionnaireModal; 